import { observable, action } from 'mobx';
import Cookies from 'js-cookie';
import * as api from '../../services/api';
import { saveToLocalStorage, getFromLocalStorage } from '../../services/utils';

const { BrowserPrint } = window;
const MANAGER_STOCK_REASON_ROLES = ['business_manager', 'territory_manager'];

const normalizeRole = (role) => (
    typeof role === 'string' ? role.toLowerCase().replace(/\s+/g, '_') : ''
);

class UserStore {
    @observable authedUser = null;
    @observable userRole = null;
    @observable position = getFromLocalStorage('position') || null;
    @observable tempToken = null;
    @observable userPhone = null;
    @observable userId = null;
    @observable loading = false;
    @observable hubsList = [];
    @observable selectedHub = getFromLocalStorage('hub') || '';
    // @observable pages = [
    //     'inventory',
    //     'packing',
    //     'cooking',
    //     'insta-service',
    //     'attendance-scan',
    //     'all-orders',
    //     'operation-dashboard',
    // ];
    @observable currentPage = 'inventory';
    @observable message = '';
    @observable isPrinterConnected =
        getFromLocalStorage('printerStatus') || false;
    @observable isCurrentPageFullScreen = false;

    @action checkPrinterStatus = () => {
        try {
            const hostname = window && window.location ? window.location.hostname : '';
            const hasCloudPrinters =
                this.selectedHub && (this.selectedHub.kitchenPrinterId || this.selectedHub.dispatchPrinterId);

            // Avoid calling local BrowserPrint service in local dev to prevent CORS warnings
            if (hostname === 'localhost' || hostname === '127.0.0.1') {
                this.setPrinterStatus(!!hasCloudPrinters);
                return;
            }

            if (!BrowserPrint || typeof BrowserPrint.getLocalDevices !== 'function') {
                this.setPrinterStatus(!!hasCloudPrinters);
                return;
            }

            BrowserPrint.getLocalDevices(
                (device_list) => {
                    if (
                        (device_list && device_list.length) ||
                        hasCloudPrinters
                    ) {
                        this.setPrinterStatus(true);
                    } else {
                        this.setPrinterStatus(false);
                    }
                },
                undefined,
                'printer'
            );
        } catch (e) {
            const hasCloudPrinters =
                this.selectedHub && (this.selectedHub.kitchenPrinterId || this.selectedHub.dispatchPrinterId);
            this.setPrinterStatus(!!hasCloudPrinters);
        }
    };

    @action handleSetFullscreen = (fullScreenState) => {
        if (fullScreenState) {
            this.isCurrentPageFullScreen = fullScreenState;
        } else {
            this.isCurrentPageFullScreen = !this.isCurrentPageFullScreen;
        }
    };

    @action handleExitFullscreen = () => {
        this.isCurrentPageFullScreen = false;
    };

    setPrinterStatus = (status) => {
        saveToLocalStorage('printerStatus', status);
        this.isPrinterConnected = status;
    };

    canUseManagerStockReason = () => {
        return [this.userRole, this.position].some((role) =>
            MANAGER_STOCK_REASON_ROLES.includes(normalizeRole(role))
        );
    };

    getMinutesUntilMidnight() {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);
    
        return Math.floor((midnight - now) / (1000 * 60));
    }

    @action login = async (data) => {
        this.loading = true;
        try {
            const res = await api.login(data);
            if (res.requires_otp) {
                if (!res.otp_sent) {
                    return { requiresOtp: true, error: true };
                } else {
                    this.userPhone = res.phone_no;
                    this.userId = res.user_id;
                    return { requiresOtp: true, error: false };
                }
            } else {
                Cookies.set("token", res.access_token, { expires: 30 });
                const jwtToken = res.jwt_token;
                saveToLocalStorage("jwtToken", jwtToken);
                saveToLocalStorage("loggedInUserName", res.name);
                this.userRole = res.user_role;
                this.authedUser = res.access_token;
                return { requiresOtp: false };
            }
        } catch (err) {
            this.message = err.message;
        } finally {
            this.loading = false;
        }
    };

    @action verifyOtp = async (otp) => {
        this.loading = true;
        try {
            const res = await api.verifyOtp({ otp: otp, phone_no: this.userPhone, user_id: this.userId });
            Cookies.set("token", res.access_token, { expires: this.getMinutesUntilMidnight() / (24 * 60) });
            this.position = res.position;
            saveToLocalStorage("position", this.position);
            const jwtToken = res.jwt_token;
            saveToLocalStorage("jwtToken", jwtToken);
            this.authedUser = res.access_token;
            saveToLocalStorage("loggedInUserName", res.name);
            return true;
        } catch (err) {
            this.message = err.message;
        } finally {
            this.loading = false;
        }
    };

    @action logout = () => {
        Cookies.remove('token');
        localStorage.clear();
        window.location.replace('/');
    };

    @action handleGetHubs = () => {
        this.loading = true;
        let hubsList = [];
        return api.getHubs()
            .then((res) => {
                if (res.length > 0) {
                    res.forEach((r) => {
                        const name = r.pickup_address && r.pickup_address.name
                        const hubsObj = {
                            value: r.code,
                            label: name,
                            city: r.city,
                            kitchenPrinterId: r.kitchen_printer_id,
                            dispatchPrinterId: r.dispatch_printer_id,
                            isOnDemandProduction: true,
                            pickupAddress: r.pickup_address,
                        };
                        hubsList.push(hubsObj);
                    });
                }
                this.hubsList = hubsList;
                saveToLocalStorage('hubsList', hubsList);
            })
            .catch((err) => {
                this.message = err.message;
            })
            .finally(() => (this.loading = false));
    };

    @action setSelectedHub = (hub) => {
        this.selectedHub = hub;
        if (
            this.selectedHub.kitchenPrinterId ||
            this.selectedHub.dispatchPrinterId
        ) {
            this.setPrinterStatus(true);
        }
        saveToLocalStorage('hub', hub);
    };

    @action setAuthentication = (token, hubCode) => {
        this.authedUser = token;
        const hub = this.hubsList.find(h => h.value === hubCode);
        this.selectedHub = hub;
        saveToLocalStorage('hub', hub);
    }

    @action setPage = (page) => {
        this.currentPage = page;
    };

    @action setMessage = (message) => {
        this.message = message;
    };
}

const store = new UserStore();
export default store;
